import React, { useState, useEffect, useCallback } from 'react';
import { UPSELL_CONFIGS, type ExpressUpsellVariant } from '../../utils/expressUpsellConfig';
import { getSecondsUntilCutoff } from '../../../../ChooseTreatmentVariants/FreeDosageChooseTreatmentV2/components/expressDeliveryUtils';
import { translate } from '../../../../../utils/translator';
import './ExpressDeliveryUpsellPopup.css';

interface ExpressDeliveryUpsellPopupProps {
  isVisible: boolean;
  variant: ExpressUpsellVariant;
  onApply: () => void;
  onContinue: () => void;
  onClose: () => void;
  cutoffTime?: string;
  language?: string;
}

const formatPrice = (cents: number): string => {
  const euros = cents / 100;
  return `${euros.toFixed(2).replace('.', ',')} €`;
};

const ExpressDeliveryUpsellPopup: React.FC<ExpressDeliveryUpsellPopupProps> = ({
  isVisible,
  variant,
  onApply,
  onContinue,
  onClose,
  cutoffTime,
  language,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const [countdownDisplay, setCountdownDisplay] = useState<string | null>(null);

  const config = UPSELL_CONFIGS[variant];
  const t = (key: string) => translate(language, 'shipping', `ExpressDeliveryUpsellPopup/${key}`);

  const heroTitle = variant === 'same_day'
    ? t('same_day_hero_title')
    : t('wait_less_hero_title');

  const bodyTitle = t(`${variant}_body_title`);
  const bodyDescription = variant === 'same_day'
    ? null
    : t(`${variant}_body_description`);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setTimeout(() => setAnimationClass('express-upsell-fade-in'), 10);
    } else if (shouldRender) {
      setAnimationClass('express-upsell-fade-out');
      setTimeout(() => {
        setShouldRender(false);
        setAnimationClass('');
      }, 300);
    }
  }, [isVisible, shouldRender]);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isVisible]);

  useEffect(() => {
    if (!config.hasCountdown || !cutoffTime) {
      setCountdownDisplay(null);
      return;
    }

    const updateCountdown = () => {
      const seconds = getSecondsUntilCutoff(cutoffTime);
      if (seconds === null) {
        setCountdownDisplay(null);
        return;
      }
      const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
      const ss = String(seconds % 60).padStart(2, '0');
      setCountdownDisplay(`${mm}:${ss}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [config.hasCountdown, cutoffTime]);

  const handleBackgroundClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  if (!shouldRender) return null;

  return (
    <div className="express-upsell-overlay" onClick={handleBackgroundClick}>
      <div className={`express-upsell-container ${animationClass}`} onClick={handleContentClick}>

        {/* Hero Banner */}
        <div className="express-upsell-hero">
          <img
            className="express-upsell-hero-image"
            src="/express-delivery-person.png"
            alt=""
          />

          <button type="button" className="express-upsell-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div className="express-upsell-hero-text">
            <div className={`express-upsell-hero-title${variant === 'same_day' ? ' express-upsell-hero-title--same-day' : ''}`}>
              {heroTitle}
            </div>

            {config.hasNewLabel && (
              <span className="express-upsell-new-label">{t('new_label')}</span>
            )}
          </div>

          <div className="express-upsell-price-badge">
            <span className="express-upsell-price-badge-text">{formatPrice(config.priceCents)}</span>
          </div>
        </div>

        {/* Body */}
        <div className="express-upsell-body">
          <h3 className="express-upsell-body-title">{bodyTitle}</h3>

          {config.hasCountdown && countdownDisplay && (
            <div className="express-upsell-countdown">
              <span className="express-upsell-countdown-label">{t('cutoff_label')}</span>
              <span className="express-upsell-countdown-time">{countdownDisplay}</span>
            </div>
          )}

          {bodyDescription && (
            <p className="express-upsell-body-description">{bodyDescription}</p>
          )}

          <button type="button" className="express-upsell-btn-apply" onClick={onApply}>
            <span className="express-upsell-btn-apply-text">{t('apply_button')}</span>
          </button>

          <button type="button" className="express-upsell-btn-continue" onClick={onContinue}>
            <span className="express-upsell-btn-continue-text">{t('continue_with_dhl')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpressDeliveryUpsellPopup;
